// Feasibility Input Validation Schema using Zod
// Ensures all feasibility calculations receive valid, properly typed data

import { z } from 'zod';

// Helper schemas for enum validation
const yesNoUnknownSchema = z.enum(['Yes', 'No', 'Unknown']);
// eslint-disable-next-line no-unused-vars
const programSchema = z.enum(['TAMA38_1', 'TAMA38_2', 'PINUY_BINUY']);

// Main feasibility input validation schema
export const feasibilityInputSchema = z.object({
  // Identity and location
  address: z.string().min(1, "Address is required"),
  municipalityName: z.string().min(1, "Municipality name is required"),
  municipalityCode: z.string().optional(),
  
  // Parcel information
  gush: z.string().optional(),
  helka: z.string().optional(),
  lotAreaSqm: z.number().positive("Lot area must be positive").optional(),
  
  // Existing building data (null when data is insufficient to estimate)
  existingUnits: z.number().int().positive("Must have at least 1 existing unit").nullable(),
  avgExistingUnitAreaSqm: z.number().positive("Average unit area must be positive").nullable(),
  pre1980Eligibility: yesNoUnknownSchema,
  constructionYear: z.number().int().min(1900).max(new Date().getFullYear()).nullable().optional(),
  constructionYearConfidence: z.string().optional(),
  yearEstimated: z.boolean().optional(),
  landUse: z.string().nullable().optional(),
  tama38Zone: z.object({
    layerName: z.string().nullable().optional(),
    zoneDescription: z.string().nullable().optional(),
    documentLink: z.string().nullable().optional()
  }).nullable().optional(),
  
  // Planning and policy flags
  tama38ValidInMunicipality: yesNoUnknownSchema,
  declaredPinuyBinuyArea: yesNoUnknownSchema,
  conservationListed: z.boolean().optional(),
  section7778Plans: z.array(z.object({
    planNumber: z.string(),
    title: z.string().optional(),
    status: z.string().optional(),
    approvalDate: z.string().optional()
  })).optional(),
  section23Plans: z.array(z.object({
    planNumber: z.string(),
    title: z.string().optional(),
    status: z.string().optional(),
    approvalDate: z.string().optional()
  })).optional(),
  buildingConservationStatus: z.object({
    hasConservation: z.boolean(),
    checked: z.boolean(),
    buildingId: z.string().optional(),
    flags: z.object({
      conservation: z.boolean(),
      municipal: z.boolean(),
      dangerous: z.boolean(),
      classified: z.boolean()
    }).optional(),
    conservationDocs: z.object({
      photos: z.boolean(),
      historicalPlans: z.boolean(),
      correspondence: z.boolean(),
      plans: z.boolean()
    }).optional()
  }).optional().nullable(),
  
  // Development rights and capacity
  estimatedNewGFAsqm: z.number().positive("Estimated new GFA must be positive"),
  saleableRatio: z.number().min(0.6, "Saleable ratio must be at least 60%").max(0.95, "Saleable ratio cannot exceed 95%"),
  
  // Market pricing
  expectedSalePricePerSqmNIS: z.number().positive("Sale price must be positive"),
  
  // Cost structure (תקן 21 standard costs)
  constructionCostPerSqmNIS: z.number().positive("Construction cost must be positive"),
  parkingCostPerNewUnitNIS: z.number().nonnegative("Parking cost cannot be negative"),
  relocationCostPerExistingUnitNIS: z.number().nonnegative("Relocation cost cannot be negative"),
  feesLeviesPermitsTotalNIS: z.number().nonnegative("Fees and levies cannot be negative"),
  bettermentLevyRate: z.number().min(0).max(1).optional(),
  bettermentLevySource: z.string().optional(),
  bettermentLevyConfidence: z.string().optional(),
  
  // Owner benefits
  ownerBonusAreaPerUnitSqm: z.number().min(0, "Owner bonus area cannot be negative"),
  
  // Modeling parameters
  targetDeveloperProfitPct: z.number().min(0, "Target profit cannot be negative").max(1, "Target profit cannot exceed 100%").optional(),
  unitNetGfaHeuristicSqm: z.number().min(60, "Unit size too small").max(120, "Unit size too large").default(95)
});

// Validation helper functions
export function validateFeasibilityInput(input) {
  try {
    return {
      success: true,
      data: feasibilityInputSchema.parse(input)
    };
  } catch (error) {
    return {
      success: false,
      error: error.errors || error.message
    };
  }
}
